import {
  Extension,
  getTextBetween,
  getTextSerializersFromSchema,
} from "@tiptap/core";
import { DOMSerializer, Fragment, type Node, Slice } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

/** Emit boundaries for visible text blocks, never their list/quote wrappers. */
function selectedText(view: EditorView): string {
  const { doc, selection, schema } = view.state;
  const { from, to } = selection;
  const textSerializers = getTextSerializersFromSchema(schema);
  const blocks: string[] = [];
  let previousItem: Node | undefined;

  doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isTextblock) return;

    const $start = doc.resolve(pos + 1);
    let item: Node | undefined;
    let marker = "";
    let listDepth = 0;
    for (let depth = 1; depth < $start.depth; depth++) {
      const ancestor = $start.node(depth);
      if (ancestor.type.name === "listItem") item = ancestor;
      if (
        ancestor.type.name === "bulletList" ||
        ancestor.type.name === "orderedList"
      ) {
        listDepth++;
        marker =
          ancestor.type.name === "orderedList"
            ? `${ancestor.attrs.start + $start.index(depth)}. `
            : ancestor.attrs.dashed
              ? "– "
              : "• ";
      }
    }

    if (blocks.length) {
      // Sibling/nested items share a line boundary. Separate paragraphs (even
      // empty ones or additional paragraphs in one item) retain paragraph breaks.
      blocks.push(
        item && previousItem && item !== previousItem ? "\n" : "\n\n",
      );
    }
    const text = getTextBetween(
      node,
      {
        from: Math.max(0, from - pos - 1),
        to: Math.min(node.content.size, to - pos - 1),
      },
      { textSerializers },
    );
    // A word/phrase selected within one paragraph is inline, not a new list.
    const includeMarker =
      item &&
      $start.node($start.depth - 1) === item &&
      $start.index($start.depth - 1) === 0 &&
      from <= pos + 1 &&
      (!selection.$from.sameParent(selection.$to) ||
        (from === pos + 1 && to === pos + 1 + node.content.size));
    blocks.push(
      (includeMarker ? "  ".repeat(listDepth - 1) + marker : "") + text,
    );
    previousItem = item;
    return false;
  });
  return blocks.join("");
}

/** Keep the original ordinal when a selection begins partway through a list. */
function selectedListStarts(slice: Slice, view: EditorView): Slice {
  const { $from } = view.state.selection;
  const adjust = (fragment: Fragment, depth: number): Fragment => {
    if (depth > slice.openStart || !fragment.firstChild) return fragment;
    const first = fragment.firstChild;
    const original = $from.node(depth);
    const attrs =
      first.type.name === "orderedList" && original.type === first.type
        ? { ...first.attrs, start: first.attrs.start + $from.index(depth) }
        : first.attrs;
    const replacement = first.type.create(
      attrs,
      adjust(first.content, depth + 1),
      first.marks,
    );
    return fragment.replaceChild(0, replacement);
  };
  return new Slice(adjust(slice.content, 1), slice.openStart, slice.openEnd);
}

class SelectedHtmlSerializer extends DOMSerializer {
  serializeFragment(
    fragment: Fragment,
    options: { document?: Document } = {},
    target?: HTMLElement | DocumentFragment,
  ): HTMLElement | DocumentFragment {
    const dom = super.serializeFragment(fragment, options, target);
    // The editor's scoped CSS does not travel on the clipboard. Preserve the
    // LI/P structure and marks, but carry its zero paragraph margins with it.
    dom.querySelectorAll<HTMLElement>("li > p").forEach((paragraph) => {
      paragraph.style.margin = "0";
    });
    dom.querySelectorAll<HTMLElement>("ul.list-dashed").forEach((list) => {
      list.style.listStyleType = '"–  "';
    });
    // Empty paragraphs need a line box in generic rich-text paste targets.
    dom.querySelectorAll("p:empty").forEach((paragraph) => {
      paragraph.appendChild(paragraph.ownerDocument.createElement("br"));
    });
    return dom;
  }
}

export const SelectedTextClipboard = Extension.create({
  name: "selectedTextClipboard",
  // Win over Tiptap's core clipboardTextSerializer without altering getText(),
  // persisted HTML, plain-text editors, or the explicit whole-note copy action.
  priority: 1000,
  addProseMirrorPlugins() {
    const serializer = DOMSerializer.fromSchema(this.editor.schema);
    return [
      new Plugin({
        key: new PluginKey("selectedTextClipboard"),
        props: {
          clipboardTextSerializer: (_slice, view) => selectedText(view),
          clipboardSerializer: new SelectedHtmlSerializer(
            serializer.nodes,
            serializer.marks,
          ),
          transformCopied: selectedListStarts,
        },
      }),
    ];
  },
});
